/**
 * LikesRepo — typed repository over {@link DocStorePort} for
 * `creations/{id}/likes/{uid}`. Existence of the doc == the user liked the
 * creation. Both operations are IDEMPOTENT (double-like / double-unlike are
 * no-ops) and OWN-DOC only (the caller can only like as themselves — the rules
 * enforce this too).
 *
 * NOTE: this repo writes ONLY the like doc. The `Creation.likesCount` rollup is
 * maintained by a trusted function reacting to like-doc create/delete (per the
 * spec: "count mirrored by function") — the client must not author the counter,
 * so it is intentionally NOT touched here.
 */
import { type DocStorePort, type Uid } from '@djgg/core';
import { GROUPS, likeDoc, likesCollection } from '../paths.js';
import type { Like } from '../types.js';

export class LikesRepo {
  constructor(private readonly store: DocStorePort) {}

  /** True when `uid` currently likes `creationId`. */
  async isLiked(creationId: string, uid: Uid): Promise<boolean> {
    return (await this.store.getDoc<Like>(likeDoc(creationId, uid))) !== null;
  }

  /**
   * Like a creation as `uid`. Idempotent: if the like already exists this is a
   * no-op and returns false (nothing changed); returns true when a new like was
   * written.
   */
  async like(creationId: string, uid: Uid): Promise<boolean> {
    if (await this.isLiked(creationId, uid)) return false;
    await this.store.setDoc<Like & Record<string, unknown>>(likeDoc(creationId, uid), {
      uid,
      createdAt: this.store.serverTimestamp() as unknown as number,
    });
    return true;
  }

  /**
   * Unlike a creation as `uid`. Idempotent: returns true when an existing like
   * was removed, false when there was nothing to remove.
   */
  async unlike(creationId: string, uid: Uid): Promise<boolean> {
    if (!(await this.isLiked(creationId, uid))) return false;
    await this.store.deleteDoc(likeDoc(creationId, uid));
    return true;
  }

  /** List all likers of a creation (for admin/inspection). */
  async list(creationId: string): Promise<Like[]> {
    return this.store.query<Like>(likesCollection(creationId));
  }

  /** Likes GIVEN by `uid` across every creation (the `likes_given` badge
   *  metric — cross-parent, backed by the 0023 `likes(uid)` index). */
  async countByUser(uid: Uid): Promise<number> {
    return this.store.countGroup(GROUPS.likes, [['uid', '==', uid]]);
  }
}
